import { useEffect, useState } from 'react';

/**
 * Hash routing in a dozen lines. A router dependency would be most of the app's
 * bundle for four screens; what actually matters on a phone is that the hardware
 * back button steps back through the deck you just opened.
 */

/**
 * "Guildmates" throughout the interface. The data model still calls them
 * pilots, because that is the word the spec uses and Pilot Elo is a named
 * concept in it — but nobody sitting at the table should have to learn that
 * vocabulary to use the app.
 */
export type Route =
  | { name: 'standings' }
  | { name: 'log' }
  | { name: 'decks' }
  | { name: 'guildmates' }
  | { name: 'deck'; id: string };

export function parse(hash: string): Route {
  const path = hash.replace(/^#\/?/, '');
  const [head, id] = path.split('/');
  switch (head) {
    case 'log':
      return { name: 'log' };
    case 'decks':
      return { name: 'decks' };
    case 'guildmates':
    // This tab has been through two earlier names; keep old links working.
    case 'players':
    case 'pilots':
      return { name: 'guildmates' };
    case 'deck':
      return id ? { name: 'deck', id } : { name: 'decks' };
    case 'standings':
      return { name: 'standings' };
    default:
      // Opening the app on game night means logging a game, so that is where
      // it opens. Standings are what you look at afterwards.
      return { name: 'log' };
  }
}

export function href(route: Route): string {
  return route.name === 'deck' ? `#/deck/${route.id}` : `#/${route.name}`;
}

export function navigate(route: Route): void {
  window.location.hash = href(route);
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parse(window.location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parse(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}
